/**
 * Reactivate a student: eligibility gate, parent block, then preview.
 */
import * as React from 'react';
import { ConfirmModal } from '@/components/common/modals/ConfirmModal';
import { InfoModal } from '@/components/common/modals/InfoModal';
import {
  useGetStudentRemoteReactivationEligibility,
  useReactivateStudentRemote,
} from '@/hooks/useUsers';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  formatReactivationPreview,
  getStudentReactivationBlockMessage,
} from './student-form-utils';

/** Props for {@link ReactivateStudentModal}. */
interface ReactivateStudentModalProps {
  open: boolean;
  userId: string;
  companyId: string;
  onClose: () => void;
  onViewParent?: (parentUserId: string) => void;
}

/** Reactivate a student: eligibility gate, parent block, then preview. */
export const ReactivateStudentModal: React.FC<Readonly<ReactivateStudentModalProps>> = ({
  open,
  userId,
  companyId,
  onClose,
  onViewParent,
}) => {
  const eligibility = useGetStudentRemoteReactivationEligibility(userId, companyId, {
    enabled: open && !!userId,
  });
  const reactivate = useReactivateStudentRemote();
  const parentUserId = eligibility.data?.parentUserId;

  async function handleConfirm(): Promise<void> {
    await reactivate.mutateAsync({ userId, companyId });
  }

  /** Close this modal and open the parent when the block reason is `parent_reactivatable`. */
  function handleViewParent(): void {
    if (parentUserId) onViewParent?.(parentUserId);
    onClose();
  }

  /** Status / situation before → after, plus the recalc / not-auto-active warnings. */
  function renderPreview(): React.ReactNode {
    const preview = eligibility.data?.preview;
    if (!preview) return null;
    const labels = formatReactivationPreview(preview);
    return (
      <div className="space-y-2 text-sm" style={{ color: 'var(--color-1-nuance-1)' }}>
        <p>
          Statut : {labels.currentStatus} → {labels.nextStatus}
        </p>
        <p>
          Situation : {labels.currentSituation} → {labels.nextSituation}
        </p>
        <p>{getErrorMessage('reactivationConfirmRecalc')}</p>
        {preview.nextStatus !== 'active' ? (
          <p>{getErrorMessage('reactivationNotAutoActive')}</p>
        ) : null}
      </div>
    );
  }

  if (!open) return null;

  if (eligibility.isLoading) {
    return (
      <InfoModal
        open
        onClose={onClose}
        title="Réactivation"
        size="md"
        sections={[{ id: 'loading', content: 'Vérification des conditions de réactivation…' }]}
        actions={[{ id: 'close', label: 'Fermer', onClick: onClose, preset: 'modal-cancel' }]}
      />
    );
  }

  if (eligibility.isError) {
    return (
      <InfoModal
        open
        onClose={onClose}
        title="Réactivation impossible"
        size="md"
        sections={[{ id: 'error', content: getErrorMessage('reactivationEligibilityFailed') }]}
        actions={[{ id: 'close', label: 'Fermer', onClick: onClose, preset: 'modal-cancel' }]}
      />
    );
  }

  if (!eligibility.data?.canReactivate) {
    const reason = eligibility.data?.reason;
    const viewParentAction =
      reason === 'parent_reactivatable' && parentUserId
        ? [
            {
              id: 'view-parent',
              label: 'Voir le parent',
              onClick: handleViewParent,
              preset: 'modal-confirm' as const,
            },
          ]
        : [];
    return (
      <InfoModal
        open
        onClose={onClose}
        title="Réactivation impossible"
        size="md"
        sections={[{ id: 'blocked', content: getStudentReactivationBlockMessage(reason) }]}
        actions={[
          { id: 'close', label: 'Fermer', onClick: onClose, preset: 'modal-cancel' },
          ...viewParentAction,
        ]}
      />
    );
  }

  return (
    <ConfirmModal
      open
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Réactiver cet élève"
      description={getErrorMessage('reactivationConfirmRecalc')}
      confirmText="Réactiver"
      variant="primary"
      isLoading={reactivate.isPending}
    >
      {renderPreview()}
    </ConfirmModal>
  );
};
